import { Form, SchemaFormHook, useForm, useFormContext } from "@/components/form";
import { usePrinter } from "@/components/print";
import { getAllClientes } from "@/features/auxiliar/clientes/api/get-all-clientes";
import { getCliente } from "@/features/auxiliar/clientes/api/get-cliente";
import { Venta } from "@/shared/types/venta";
import { Stack, StackProps } from "@mui/material";
import { z } from "zod/v4";
import VentaPrint from "./venta-print-factura";
import { usePutVentaNumComprobanteMutation } from "../api/put-venta-num-comprobante";
import AddIcon from '@mui/icons-material/Add';
import { Drawer, ModalHeader, useOpenModal } from "@/components/modal";
import { AddClienteMutation } from "@/features/auxiliar/clientes/api/add-cliente";
import useHasPermisos from "@/features/auth/hooks/useHasPermisos";
import { Permiso } from "@/shared/types/permiso";
import { notify } from "@/shared/notification";
import useMonetizacion from "@/features/monetizacion/hooks/use-monetizacion";
import { useConfigurationContext } from "@/features/configuration/contexts/configuration-context";
import SimpleClienteForm, { simpleClienteFormSchema } from "@/features/auxiliar/clientes/components/simple-cliente-form";

enum VentaPrintType {
    Factura,
    Ticket
}

export const ventaPrintFormSchema = z.discriminatedUnion("type", [
    z.object({
        type: z.literal(VentaPrintType.Factura),
        numComprobante: z.string(),
        clienteId: z.string(),
    }),
    z.object({
        type: z.literal(VentaPrintType.Ticket),
        monedaId: z.string(),
    })
])

export interface VentaPrintFormProps extends StackProps {
    venta: Venta
    ticketOnly?: boolean
}

export default function VentaPrintForm({
    venta,
    ticketOnly = false,
    ...props
}: VentaPrintFormProps) {
    const monedaBase = useMonetizacion().monedaBase;
    const genericClientId = useConfigurationContext().genericClientId;

    const printer = usePrinter();
    const mutation = usePutVentaNumComprobanteMutation(venta.id);

    const form = useForm({
        onSubmit: async ({ value }) => {
            if (value.type === VentaPrintType.Factura) {
                const nuevaVenta = await mutation.mutateAsync(value);
                await printer.print({
                    documentTitle: `Venta N°${nuevaVenta.numeroVenta}`,
                    printComponent: <VentaPrint venta={nuevaVenta} variant={"factura"} />
                })
            } else {
                await printer.print({
                    documentTitle: `Venta N°${venta.numeroVenta}`,
                    printComponent: <VentaPrint monedaId={value.monedaId} venta={venta} variant={"ticket"} />,
                    pageStyle: "@page { size: 80mm auto; margin: 0; }"
                })
            }
        },
        defaultValues: {
            type: ticketOnly ? VentaPrintType.Ticket : VentaPrintType.Factura,
            numComprobante: venta.numComprobante || "",
            clienteId: venta.clienteId || genericClientId,
            monedaId: monedaBase.id
        } as z.infer<typeof ventaPrintFormSchema>,
        validators: {
            onSubmit: ventaPrintFormSchema
        },
        submitOnce: true,
        closeModalOnSubmit: true,
        preventCloseModalWhileSubmitting: false
    })

    return <Form form={form} gap={3} {...props}>
        <Form.Select name="type" label="Tipo" disabled={ticketOnly}>
            <Form.Select.Option value={VentaPrintType.Factura}>Factura</Form.Select.Option>
            <Form.Select.Option value={VentaPrintType.Ticket}>Ticket</Form.Select.Option>
        </Form.Select>
        <form.Subscribe selector={state => state.values.type}>
            {type => type === VentaPrintType.Factura ? <FacturaFields /> : <TicketField venta={venta} />}
        </form.Subscribe>
        <Form.SubmitButton>Imprimir</Form.SubmitButton>
        <printer.Printer />
    </Form>
}

function FacturaFields() {
    const openModal = useOpenModal();
    const hasWriteClientePermiso = useHasPermisos(Permiso.WriteClientes);
    const form = useFormContext();

    return <>
        <Stack gap={2} direction={"row"} alignItems={"center"}>
            <Stack flex={1}>
                <Form.ModelComboBox name="clienteId" label="Cliente" get={getCliente} getAll={getAllClientes} />
            </Stack>
            {hasWriteClientePermiso && <Stack>
                <Form.IconButton onClick={() => openModal("addCliente")}>
                    <AddIcon />
                </Form.IconButton>
            </Stack>}
        </Stack>
        <Form.TextField name="numComprobante" label="N° de Comprobante" />

        <Drawer code="addCliente">
            <ModalHeader>Agregar Cliente</ModalHeader>
            <AddClienteMutation>
                {mutation => <SchemaFormHook
                    schema={simpleClienteFormSchema}
                    onSubmit={async ({ value }) => {
                        const response = await mutation.mutateAsync(value);
                        notify("El cliente se ha agregado correctamente.");
                        form.setFieldValue("clienteId", response.id);
                    }}
                    closeModalOnSubmit
                    submitOnce
                >
                    {form => <SimpleClienteForm form={form} margin={3} />}
                </SchemaFormHook>}
            </AddClienteMutation>
        </Drawer>
    </>
}

function TicketField({ venta }: { venta: Venta }) {
    const cotizacionItems = venta.cotizacion.cotizacionItems;

    return <>
        <Form.Select name="monedaId" label="Moneda">
            {cotizacionItems.map(ci => <Form.Select.Option key={ci.monedaId} value={ci.monedaId}>{ci.monedaNombre}</Form.Select.Option>)}
        </Form.Select>
    </>
}